import { useState } from 'react';

import useFetchFilteredResults from 'js/hooks/use-fetch-filtered-results';
import { normalizeFiltersToStrings, normalizeStringsToArrays } from 'js/utils/filter-normalization';

import ContentContainer from 'components/content-container/ContentContainer';
import EmptyList from 'components/empty-list/EmptyList';
import FilterLayout from 'components/filter-layout/FilterLayout';
import Form from 'components/form/Form';
import GroupedSearchPageSectionFilterList from 'components/grouped-search-page-section-filter-list/GroupedSearchPageSectionFilterList';
import Pagination from 'components/pagination/Pagination';
import Search from 'components/search/Search';
import SearchResultGroup from 'components/search-result-group/SearchResultGroup';
import Spinner from 'components/spinner/Spinner';

import { IGroupedSearchPage } from 'source/NFR.types';

type GroupedSearchPageFilterState = Pick<
  IGroupedSearchPage,
  'results' | 'pagination' | 'resultsDescription' | 'emptyList' | 'search' | 'filterLayout' | 'sections'
>;

export interface GroupedSearchPageProps extends IGroupedSearchPage {
  hasLowerCaseSectionFilters?: boolean;
}

const GroupedSearchPage: React.FC<GroupedSearchPageProps> = ({
  emptyList,
  filterLayout,
  form,
  sections,
  pagination,
  results = [],
  resultsDescription,
  search,
  title,
  hasLowerCaseSectionFilters,
  fetchFilteredResultsEndpoint
}) => {
  const currentSectionFilter = (function () {
    const currentSection = sections.list && sections.list.find(section => section.isCurrent);
    if (currentSection) return { [currentSection.name]: currentSection.value };
  })();

  // This is a merger of <Filters> searchQueryParamter state and section filter
  const [filterQuery, setFilterQuery] = useState<Record<string, string> | undefined>(currentSectionFilter);
  const updatedFilterQuery = (filters: Record<string, (string | number)[]>) => {
    const normalizedFilters = normalizeFiltersToStrings(filters);
    setFilterQuery(prev => ({ ...prev, ...normalizedFilters }));
  };

  const [
    isLoading,
    {
      results: resultsState,
      pagination: paginationState,
      resultsDescription: resultsDescriptionState,
      emptyList: emptyListState,
      search: searchState,
      filterLayout: filterLayoutState,
      sections: sectionsState
    },
    fetchResults
  ] = useFetchFilteredResults<GroupedSearchPageFilterState>(
    {
      results,
      pagination,
      resultsDescription,
      emptyList,
      search,
      filterLayout,
      sections
    },
    fetchFilteredResultsEndpoint,
    form.endpoint
  );

  const getSearchQueryParam = (): Record<string, string> => {
    const inputName = searchState?.input?.name;
    const inputValue = searchState?.input?.value;
    return inputName && inputValue ? { [inputName]: inputValue } : {};
  };

  const fetchResultsWithSearch = (searchQueryParameter: Record<string, string | string[]>) => {
    fetchResults({ ...getSearchQueryParam(), ...filterQuery, ...searchQueryParameter });
  };

  const fetchResultsWithSection = (name: string, value: string | undefined) => {
    const searchQuery = getSearchQueryParam();

    if (value) {
      const isYearSection = name === 'year';

      if (isYearSection) {
        const nextQueryParameters: Record<string, string | string[]> = {
          ...searchQuery,
          [name]: value
        };

        setFilterQuery({ [name]: value });
        fetchResults(nextQueryParameters);
        return;
      }

      // Build query with arrays for filter values, scalar for section value
      const queryParameters: Record<string, string | string[]> = {};
      if (filterQuery) {
        for (const [key, val] of Object.entries(filterQuery)) {
          if (key === name) continue;
          queryParameters[key] = val.split(',');
        }
      }
      queryParameters[name] = value;

      // Update stored filter state (flat strings)
      const flatQueryParameters: Record<string, string> = { ...(filterQuery ?? {}), [name]: value };
      const queryParametersAsArray = normalizeStringsToArrays(flatQueryParameters);
      updatedFilterQuery(queryParametersAsArray);

      fetchResults({ ...searchQuery, ...queryParameters });
    } else {
      setFilterQuery(undefined);
      fetchResults(searchQuery);
    }
  };

  const topContent = <div className="grouped-search-page--results-description">{resultsDescriptionState}</div>;

  return (
    <Form showSubmitButton={false} {...form}>
      <ContentContainer className="grouped-search-page">
        {title && <h1 className="grouped-search-page--title">{title}</h1>}
        <Search {...searchState} />

        {sectionsState && (
          <GroupedSearchPageSectionFilterList
            list={sectionsState.list}
            fetchResultsWithSection={fetchResultsWithSection}
            hasLowerCaseSectionFilters={hasLowerCaseSectionFilters}
          />
        )}
      </ContentContainer>
      <FilterLayout
        {...filterLayoutState}
        topContent={topContent}
        fetchFilteredResults={fetchResultsWithSearch}
        isLoading={isLoading}
        includeBottomBorder={false}
        updateFilterQuery={updatedFilterQuery}
      >
        {resultsState.length > 0 ? (
          resultsState.map((result, index) => <SearchResultGroup key={result.title + String(index)} {...result} />)
        ) : (
          <EmptyList {...emptyListState} />
        )}
        <Spinner isActive={isLoading} />
        <Pagination {...paginationState} />
      </FilterLayout>
    </Form>
  );
};

GroupedSearchPage.displayName = 'GroupedSearchPage';

export default GroupedSearchPage;
